import { fetchEventSource, type EventSourceMessage } from '@microsoft/fetch-event-source';
import type { SSEEvents } from '@signal24/zyno-shared';
import { isEqual } from 'lodash';
import { onMounted, onUnmounted } from 'vue';
import { Logger } from './logger';

const logger = Logger.createScoped('SSE');

export class SSEService {
    private static enabled = false;
    private static ctrl: AbortController | null = null;
    private static connectionHeaders: Record<string, string> | null = null;
    private static connectionPromise: Promise<void> | null = null;
    private static subscriptionCache: Record<string, Set<(data: unknown) => void>> = {};
    static connId: string | null = null;

    static async connect() {
        const { getApiHeaders } = await import('../../openapi-clients/shared');

        const sseUrl = `${import.meta.env.VITE_APP_AUTH_API_URL}/sse`;
        const allHeaders = await getApiHeaders({ method: 'GET', url: sseUrl, headers: {} }, true);
        const headers = Object.fromEntries(Object.entries(allHeaders).filter(([k]) => /^(authorization|x-zs-)/i.test(k)));

        if (isEqual(this.connectionHeaders, headers)) return;
        this.connectionHeaders = headers;

        const existingAbortCtrl = this.ctrl;
        this.ctrl = new AbortController();

        this.enabled = true;
        logger.info('Connecting to SSE endpoint');

        let thisConnId: string | null = null;
        try {
            this.connectionPromise = fetchEventSource(sseUrl, {
                openWhenHidden: true,
                signal: this.ctrl.signal,
                headers,
                onopen: async response => {
                    existingAbortCtrl?.abort();

                    const connId = response.headers.get('x-sse-connection-id');
                    logger.info('Connected to SSE endpoint', { connId });
                    thisConnId = connId;
                    this.connId = connId;
                },
                onmessage: event => {
                    if (this.connId === thisConnId) {
                        this.handleMessage(event);
                    }
                },
                onerror: err => {
                    if (this.connId === thisConnId) {
                        // we need to throw the error to bypass the auto-retry
                        // we need to bypass auto-retry so that we can get fresh auth headers
                        throw err;
                    }
                }
            });
            await this.connectionPromise;
            logger.info('Disconnected from SSE endpoint', { connId: thisConnId });
        } catch (err) {
            logger.error('Error in SSE connection:', err);
        } finally {
            this.connectionPromise = null;
            this.connectionHeaders = null;
        }

        if (this.connId !== thisConnId) return;

        setTimeout(() => {
            if (this.enabled) {
                this.connect();
            }
        }, 2000);
    }

    static async disconnect() {
        this.enabled = false;
        this.ctrl?.abort();
        await this.connectionPromise;
    }

    // oxlint-disable-next-line no-explicit-any
    static subscribe(event: string, handler: (data: any) => void) {
        this.subscriptionCache[event] = this.subscriptionCache[event] ?? new Set();
        this.subscriptionCache[event].add(handler);
    }

    // oxlint-disable-next-line no-explicit-any
    static unsubscribe(event: string, handler: (data: any) => void) {
        this.subscriptionCache[event]?.delete(handler);
    }

    static createEventChannel<T extends keyof SSEEvents>(eventName: T) {
        return {
            subscribe(listener: (data: SSEEvents[T]) => void) {
                SSEService.subscribe(eventName, listener);
            },

            unsubscribe(listener: (data: SSEEvents[T]) => void) {
                SSEService.unsubscribe(eventName, listener);
            },

            use(listener: (data: SSEEvents[T]) => void) {
                onMounted(() => this.subscribe(listener));
                onUnmounted(() => this.unsubscribe(listener));
            }
        };
    }

    private static handleMessage(event: EventSourceMessage) {
        const subscriptions = this.subscriptionCache[event.event];
        if (subscriptions) {
            let data: unknown = null;
            try {
                data = event.data ? JSON.parse(event.data) : null;
            } catch (err) {
                logger.error('Error parsing SSE message data:', err, { event });
            }
            for (const handler of subscriptions) {
                try {
                    handler(data);
                } catch (err) {
                    logger.error('Error in SSE message handler:', err, { event });
                }
            }
        }
    }
}
